/**
 * FERP Portal - Reference Library Page
 *
 * Read-only policy lookup for staff. Searchable, categorized, role-filtered.
 * Full policy management stays in Admin → Governance.
 *
 * DRR-245: Staff-facing policy reference (separate from admin Policy management)
 */

import { useState, useMemo } from 'react';
import {
  Container,
  Title,
  Text,
  TextInput,
  SimpleGrid,
  Card,
  Badge,
  Group,
  Stack,
  Tabs,
  Accordion,
  ThemeIcon,
  Paper,
  Loader,
  Center,
  Alert,
} from '@mantine/core';
import {
  IconSearch,
  IconBook,
  IconPill,
  IconShieldCheck,
  IconAlertTriangle,
  IconUsers,
  IconClipboard,
  IconHeart,
  IconSettings,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

// Query policies (rules) from backend
const GET_POLICIES_FOR_REFERENCE = gql`
  query GetPoliciesForReference($tenantId: String!) {
    policies(tenantId: $tenantId, first: 500) {
      edges {
        node {
          id
          name
          displayName
          description
          safetyLevel
          status
          policyType
          mandateRef
        }
      }
    }
  }
`;

interface PolicyNode {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  safetyLevel?: string;
  status?: string;
  policyType?: string;
  mandateRef?: string;
}

// Category mapping based on policy name/description
const categorizePolicy = (policy: PolicyNode): string => {
  const text = `${policy.name} ${policy.displayName} ${policy.description || ''}`.toLowerCase();

  if (text.includes('medication') || text.includes('drug') || text.includes('pharmacy') || text.includes('narcotic')) {
    return 'medication';
  }
  if (text.includes('emergency') || text.includes('fire') || text.includes('evacuation') || text.includes('disaster')) {
    return 'emergency';
  }
  if (text.includes('infection') || text.includes('tb') || text.includes('communicable')) {
    return 'infection';
  }
  if (text.includes('staff') || text.includes('training') || text.includes('competenc') || text.includes('delegation')) {
    return 'staffing';
  }
  if (text.includes('resident right') || text.includes('abuse') || text.includes('neglect') || text.includes('maltreat')) {
    return 'resident';
  }
  if (text.includes('document') || text.includes('record') || text.includes('assessment')) {
    return 'documentation';
  }
  if (text.includes('safety') || text.includes('fall') || text.includes('elopement')) {
    return 'safety';
  }
  return 'general';
};

const CATEGORY_CONFIG: Record<string, { label: string; icon: typeof IconPill; color: string }> = {
  medication: { label: 'Medications', icon: IconPill, color: 'blue' },
  emergency: { label: 'Emergency', icon: IconAlertTriangle, color: 'red' },
  infection: { label: 'Infection Control', icon: IconShieldCheck, color: 'green' },
  staffing: { label: 'Staff & Training', icon: IconUsers, color: 'violet' },
  resident: { label: 'Resident Rights', icon: IconHeart, color: 'pink' },
  documentation: { label: 'Documentation', icon: IconClipboard, color: 'cyan' },
  safety: { label: 'Safety', icon: IconShieldCheck, color: 'orange' },
  general: { label: 'General', icon: IconSettings, color: 'gray' },
};

export function ReferenceLibraryPage() {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<string | null>('all');

  // Get tenant from auth context - simplified for now
  const tenantId = '2'; // Hillside Harmony

  const { data, loading, error } = useQuery(GET_POLICIES_FOR_REFERENCE, {
    variables: { tenantId },
  });

  const policies: PolicyNode[] = useMemo(() => {
    if (!data?.policies?.edges) return [];
    return data.policies.edges.map((edge: { node: PolicyNode }) => edge.node);
  }, [data]);

  // Group by category
  const policiesByCategory = useMemo(() => {
    const grouped: Record<string, PolicyNode[]> = {};
    for (const policy of policies) {
      const category = categorizePolicy(policy);
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push(policy);
    }
    return grouped;
  }, [policies]);

  // Filter by search
  const filteredPolicies = useMemo(() => {
    if (!search.trim()) return policies;
    const searchLower = search.toLowerCase();
    return policies.filter(
      (p) =>
        p.name.toLowerCase().includes(searchLower) ||
        p.displayName.toLowerCase().includes(searchLower) ||
        (p.description && p.description.toLowerCase().includes(searchLower))
    );
  }, [policies, search]);

  // Filter by category tab
  const displayPolicies = useMemo(() => {
    if (activeTab === 'all') return filteredPolicies;
    return filteredPolicies.filter((p) => categorizePolicy(p) === activeTab);
  }, [filteredPolicies, activeTab]);

  if (loading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert color="red" title="Error loading policies">
          {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={2}>
              <Group gap="xs">
                <ThemeIcon size="lg" variant="light" color="blue">
                  <IconBook size={20} />
                </ThemeIcon>
                Reference Library
              </Group>
            </Title>
            <Text c="dimmed" size="sm" mt={4}>
              Search policies and procedures. For full management, see Admin → Governance.
            </Text>
          </div>
          <Badge size="lg" variant="light">
            {policies.length} policies
          </Badge>
        </Group>

        {/* Search */}
        <TextInput
          placeholder="Search policies... (e.g., PRN medication, fall, elopement)"
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          size="md"
        />

        {/* Category Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="all">All ({filteredPolicies.length})</Tabs.Tab>
            {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
              const count = (policiesByCategory[key] || []).filter((p) =>
                !search.trim() ||
                p.name.toLowerCase().includes(search.toLowerCase()) ||
                p.displayName.toLowerCase().includes(search.toLowerCase())
              ).length;
              if (count === 0) return null;
              return (
                <Tabs.Tab key={key} value={key} leftSection={<config.icon size={14} />}>
                  {config.label} ({count})
                </Tabs.Tab>
              );
            })}
          </Tabs.List>
        </Tabs>

        {/* Results */}
        {displayPolicies.length === 0 ? (
          <Paper p="xl" withBorder>
            <Center>
              <Stack align="center" gap="xs">
                <IconInfoCircle size={32} color="gray" />
                <Text c="dimmed">No policies match your search</Text>
              </Stack>
            </Center>
          </Paper>
        ) : (
          <Accordion variant="separated">
            {displayPolicies.map((policy) => {
              const category = categorizePolicy(policy);
              const config = CATEGORY_CONFIG[category];

              return (
                <Accordion.Item key={policy.id} value={policy.id}>
                  <Accordion.Control>
                    <Group justify="space-between" wrap="nowrap" pr="md">
                      <Group gap="sm" wrap="nowrap">
                        <ThemeIcon size="sm" variant="light" color={config.color}>
                          <config.icon size={14} />
                        </ThemeIcon>
                        <div>
                          <Text fw={500} size="sm">
                            {policy.displayName || policy.name}
                          </Text>
                          {policy.mandateRef && (
                            <Text size="xs" c="dimmed">
                              {policy.mandateRef}
                            </Text>
                          )}
                        </div>
                      </Group>
                      {policy.safetyLevel === 'critical' && (
                        <Badge color="red" size="xs" variant="filled">
                          CRITICAL
                        </Badge>
                      )}
                      {policy.safetyLevel === 'high' && (
                        <Badge color="orange" size="xs" variant="light">
                          HIGH
                        </Badge>
                      )}
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="sm">
                      {policy.description && (
                        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                          {policy.description}
                        </Text>
                      )}
                      <Group gap="xs">
                        <Badge size="xs" variant="outline" color={config.color}>
                          {config.label}
                        </Badge>
                        {policy.policyType && (
                          <Badge size="xs" variant="outline">
                            {policy.policyType}
                          </Badge>
                        )}
                      </Group>
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              );
            })}
          </Accordion>
        )}
      </Stack>
    </Container>
  );
}

export default ReferenceLibraryPage;
